/**
 * Windows-/OS-Toasts über Notification API + Service Worker + Web Push.
 * Lokal wenn Tab offen; Push vom Server wenn Tab/Browser im Hintergrund.
 */

import { createElement } from 'react';
import axios from 'axios';
import toast from 'react-hot-toast';

let swRegisterPromise = null;
let pushBootstrapPromise = null;

function publicAsset(path) {
  const base = (process.env.PUBLIC_URL || '').replace(/\/$/, '');
  const clean = String(path || '').replace(/^\//, '');
  return `${base}/${clean}`;
}

export function getDesktopNotificationPermission() {
  if (typeof window === 'undefined' || typeof Notification === 'undefined') {
    return 'unsupported';
  }
  if (window.isSecureContext === false) {
    return 'unsupported';
  }
  return Notification.permission;
}

export function desktopNotificationsSupported() {
  if (typeof window === 'undefined') return false;
  if (window.isSecureContext === false) return false;
  return typeof Notification !== 'undefined';
}

/** Tab sichtbar (auch ohne Fenster-Fokus — z. B. zweiter Monitor / IDE im Vordergrund). */
export function isDocumentVisible() {
  if (typeof document === 'undefined') return true;
  if (document.hidden) return false;
  if (document.visibilityState && document.visibilityState !== 'visible') return false;
  return true;
}

/** Tab sichtbar und Browser-Fenster hat Fokus. */
export function isAppInForeground() {
  if (!isDocumentVisible()) return false;
  if (typeof document.hasFocus === 'function' && !document.hasFocus()) return false;
  return true;
}

export async function requestDesktopNotificationPermission() {
  if (!desktopNotificationsSupported()) return 'unsupported';
  if (Notification.permission === 'granted') {
    await ensureMailNotificationSW();
    return 'granted';
  }
  if (Notification.permission === 'denied') return 'denied';
  try {
    const result = await Notification.requestPermission();
    if (result === 'granted') await ensureMailNotificationSW();
    return result || 'default';
  } catch (_) {
    return getDesktopNotificationPermission();
  }
}

export function ensureMailNotificationSW() {
  if (typeof navigator === 'undefined' || !('serviceWorker' in navigator)) {
    return Promise.resolve(null);
  }
  if (window.isSecureContext === false) return Promise.resolve(null);
  if (!swRegisterPromise) {
    swRegisterPromise = navigator.serviceWorker
      .register(publicAsset('sw-mail-notifications.js'), { scope: '/' })
      .then(async (reg) => {
        try {
          await navigator.serviceWorker.ready;
        } catch (_) {
          /* ignore */
        }
        try {
          await reg.update();
        } catch (_) {
          /* ignore */
        }
        return reg;
      })
      .catch((err) => {
        console.warn('Mail-Notification-SW fehlgeschlagen:', err?.message || err);
        swRegisterPromise = null;
        return null;
      });
  }
  return swRegisterPromise;
}

function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - (String(base64String).length % 4)) % 4);
  const base64 = (String(base64String) + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw = window.atob(base64);
  const out = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i += 1) out[i] = raw.charCodeAt(i);
  return out;
}

export function webPushSupported() {
  if (!desktopNotificationsSupported()) return false;
  if (typeof navigator === 'undefined' || !('serviceWorker' in navigator)) return false;
  if (typeof window === 'undefined' || !('PushManager' in window)) return false;
  return true;
}

/** Browser-Push-Abo anlegen und am Server speichern (für Hintergrund). */
export async function ensureWebPushSubscription() {
  if (!webPushSupported()) return { ok: false, reason: 'unsupported' };
  if (Notification.permission !== 'granted') return { ok: false, reason: 'permission' };

  const reg = await ensureMailNotificationSW();
  if (!reg?.pushManager) return { ok: false, reason: 'no-sw' };

  let publicKey = '';
  try {
    const res = await axios.get('/api/push/vapid-public-key');
    publicKey = String(res.data?.publicKey || '');
  } catch (_) {
    return { ok: false, reason: 'vapid' };
  }
  if (!publicKey) return { ok: false, reason: 'vapid' };

  let sub = await reg.pushManager.getSubscription();
  if (!sub) {
    try {
      sub = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(publicKey),
      });
    } catch (err) {
      console.warn('Push-Subscribe fehlgeschlagen:', err?.message || err);
      return { ok: false, reason: 'subscribe', error: err?.message };
    }
  }

  try {
    await axios.post('/api/push/subscribe', { subscription: sub.toJSON() });
  } catch (err) {
    return { ok: false, reason: 'save', error: err?.response?.data?.error || err?.message };
  }
  return { ok: true, subscription: sub };
}

/**
 * SW + Push-Abo herstellen, wenn Browser-Berechtigung schon „granted“ ist
 * (z. B. nach Reload — ohne erneut auf „Erlauben“ klicken zu müssen).
 */
export async function bootstrapDesktopNotifications({ enabled = true } = {}) {
  if (!enabled) return { ok: false, reason: 'disabled' };
  if (!desktopNotificationsSupported()) return { ok: false, reason: 'unsupported' };
  if (Notification.permission !== 'granted') return { ok: false, reason: 'permission' };

  if (!pushBootstrapPromise) {
    pushBootstrapPromise = (async () => {
      await ensureMailNotificationSW();
      if (!webPushSupported()) return { ok: true, push: false };
      const push = await ensureWebPushSubscription();
      return { ok: true, push: !!push.ok, pushResult: push };
    })().finally(() => {
      // Erneut erlauben nach Fehlern / Settings-Änderungen
      setTimeout(() => {
        pushBootstrapPromise = null;
      }, 15_000);
    });
  }
  return pushBootstrapPromise;
}

/** Server-Push-Test (kommt auch bei geschlossenem Tab an, wenn Abo aktiv). */
export async function sendServerPushTest() {
  const sub = await ensureWebPushSubscription();
  if (!sub.ok) return sub;
  try {
    const res = await axios.post('/api/push/test');
    return { ok: true, sent: res.data?.sent, total: res.data?.total };
  } catch (err) {
    return {
      ok: false,
      reason: 'send',
      error: err?.response?.data?.error || err?.message,
    };
  }
}

function truncate(text, max = 120) {
  const s = String(text || '')
    .replace(/\s+/g, ' ')
    .trim();
  if (!s) return '';
  if (s.length <= max) return s;
  return `${s.slice(0, max - 1)}…`;
}

/** „Name <mail@x.de>“ → Name; sonst lokaler Teil oder Rohstring */
export function formatMailSender(from) {
  const raw = String(from || '').trim();
  if (!raw) return '';
  const named = raw.match(/^"?([^"<>]+)"?\s*<[^>]+>$/);
  if (named) return named[1].replace(/\s+/g, ' ').trim();
  const angled = raw.match(/<([^>]+)>/);
  const email = (angled ? angled[1] : raw).trim();
  if (email.includes('@')) return email.split('@')[0];
  return truncate(raw, 48);
}

/**
 * Schlichte Windows-Toast-Inhalte (wie ein Mail-Client):
 * Titel = Absender, Body = Betreff
 */
export function buildMailNotificationContent(opts = {}) {
  const count = Math.max(1, Number(opts.count) || 1);
  const sender = formatMailSender(opts.from) || 'Unbekannt';
  const subject = truncate(opts.subject, 100) || 'Kein Betreff';

  if (count === 1) {
    return { title: sender, body: subject, count };
  }
  return {
    title: `${count} neue Nachrichten`,
    body: `${sender} — ${subject}`,
    count,
  };
}

function notificationIconUrl() {
  return publicAsset('icon-192.png');
}

function notificationBadgeUrl() {
  return publicAsset('favicon-48.png');
}

async function showOsNotification(title, options) {
  // 1) Service Worker (zuverlässiger im Hintergrund)
  try {
    const reg = await ensureMailNotificationSW();
    if (reg && typeof reg.showNotification === 'function') {
      await reg.showNotification(title, options);
      return true;
    }
  } catch (err) {
    console.warn('SW-Notification fehlgeschlagen:', err?.message || err);
  }

  // 2) Fallback: Notification-Konstruktor
  try {
    const notification = new Notification(title, options);
    notification.onclick = () => {
      try {
        window.focus();
      } catch (_) {
        /* ignore */
      }
      try {
        if (options?.data?.url) window.location.assign(options.data.url);
      } catch (_) {
        /* ignore */
      }
      try {
        notification.close();
      } catch (_) {
        /* ignore */
      }
    };
    return true;
  } catch (err) {
    console.warn('Desktop-Notification fehlgeschlagen:', err?.message || err);
    return false;
  }
}

/**
 * @param {{ count?: number, subject?: string, from?: string, enabled?: boolean, force?: boolean }} opts
 * @returns {Promise<boolean>}
 */
export async function notifyNewMail(opts = {}) {
  const {
    count = 1,
    subject = '',
    from = '',
    enabled = true,
    force = false,
  } = opts;

  if (!enabled) return false;
  if (!desktopNotificationsSupported()) return false;
  if (Notification.permission !== 'granted') return false;
  // OS-Toast überspringen nur wenn Fenster wirklich fokussiert ist (User sieht die App)
  if (!force && isAppInForeground()) return false;

  const { title, body } = buildMailNotificationContent({ count, subject, from });
  const icon = notificationIconUrl();
  return showOsNotification(title, {
    body,
    icon,
    badge: notificationBadgeUrl(),
    tag: 'odium-new-mail',
    renotify: true,
    silent: false,
    requireInteraction: false,
    data: { url: '/inbox' },
  });
}

function formatReminderLead(minutes) {
  const m = Number(minutes) || 0;
  if (m <= 0) return 'jetzt';
  if (m < 60) return `in ${m} Min.`;
  if (m === 60) return 'in 1 Std.';
  if (m < 1440) {
    const h = Math.round(m / 60);
    return `in ${h} Std.`;
  }
  return 'morgen';
}

/**
 * Erinnerung vor einem Kalendertermin.
 * @returns {Promise<boolean>}
 */
export async function notifyCalendarReminder(opts = {}) {
  const {
    title: eventTitle = 'Termin',
    startAt = null,
    minutesBefore = 15,
    location = '',
    enabled = true,
    force = true,
  } = opts;

  if (!enabled) return false;
  if (!desktopNotificationsSupported()) return false;
  if (Notification.permission !== 'granted') return false;
  if (!force && isAppInForeground()) return false;

  const when = startAt
    ? new Date(startAt).toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' })
    : '';
  const lead = formatReminderLead(minutesBefore);
  const title = minutesBefore <= 0 ? 'Termin beginnt' : `Termin-Erinnerung (${lead})`;
  const parts = [String(eventTitle || 'Termin').trim() || 'Termin'];
  if (when) parts.push(when);
  if (location) parts.push(String(location).trim());
  const body = parts.join(' · ');
  const icon = notificationIconUrl();
  const tag = `odium-cal-${String(opts.eventId || eventTitle)}-${minutesBefore}-${String(startAt || '')}`;
  return showOsNotification(title, {
    body,
    icon,
    badge: notificationBadgeUrl(),
    tag,
    renotify: false,
    silent: false,
    requireInteraction: false,
    data: { url: '/calendar' },
  });
}

/**
 * Schlichter In-App-Toast bei neuer Mail (sichtbarer Tab).
 */
export function toastNewMail({ count = 1, from = '', subject = '' } = {}) {
  const sender = formatMailSender(from);
  const subj = truncate(subject, 64);
  const n = Math.max(1, Number(count) || 1);
  const eyebrow = n === 1 ? 'Neue Nachricht' : `${n} neue Nachrichten`;
  const logoSrc = notificationIconUrl();

  return toast.custom(
    (t) =>
      createElement(
        'button',
        {
          type: 'button',
          onClick: () => toast.dismiss(t.id),
          className:
            'pointer-events-auto w-full max-w-xs text-left rounded-xl border border-stone-200/90 dark:border-neutral-700 bg-white dark:bg-neutral-900 shadow-lg shadow-stone-900/10 px-3 py-2.5',
        },
        createElement(
          'div',
          { className: 'flex items-start gap-2.5' },
          createElement('img', {
            src: logoSrc,
            alt: '',
            width: 28,
            height: 28,
            className: 'mt-0.5 h-7 w-7 flex-shrink-0 rounded-lg object-cover',
            draggable: false,
          }),
          createElement(
            'span',
            { className: 'min-w-0 flex-1' },
            createElement(
              'span',
              {
                className:
                  'block text-[10px] font-medium uppercase tracking-[0.12em] text-stone-400 dark:text-neutral-500',
              },
              eyebrow
            ),
            createElement(
              'span',
              {
                className:
                  'mt-0.5 block truncate text-[13px] font-semibold leading-snug text-stone-900 dark:text-stone-50',
              },
              sender || 'Unbekannt'
            ),
            createElement(
              'span',
              {
                className:
                  'mt-0.5 block truncate text-[12px] leading-snug text-stone-500 dark:text-neutral-400',
              },
              subj || 'Kein Betreff'
            )
          )
        )
      ),
    { duration: 5500, id: 'odium-new-mail-toast' }
  );
}

export function getDesktopNotificationDiagnostics() {
  const secure = typeof window !== 'undefined' && window.isSecureContext !== false;
  const hasNotif = typeof Notification !== 'undefined';
  const hasSW = typeof navigator !== 'undefined' && 'serviceWorker' in navigator;
  const perm = hasNotif ? Notification.permission : 'unsupported';
  return {
    secure,
    hasNotif,
    hasSW,
    permission: perm,
    supported: secure && hasNotif,
  };
}
